import React, { useEffect, useMemo, useState } from 'react';
import { generateClient } from 'aws-amplify/api';
import { useParams } from 'react-router-dom';
import * as queries from '../graphql/queries';
import * as mutations from '../graphql/mutations';
import { EVENT_TYPES, nowSeconds, recordOperationEvent } from '../platform/events/operationEvents';
import './RoadLocationPingPage.css';

const client = generateClient();

function normalizeToken(token) {
  return String(token || '').trim();
}

export default function RoadLocationPingPage() {
  const { token } = useParams();
  const cleanToken = useMemo(() => normalizeToken(token), [token]);
  const [call, setCall] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function loadCall() {
      if (!cleanToken) {
        setError('This location link is missing information.');
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const result = await client.graphql({
          query: queries.listRoadServiceCalls,
          variables: {
            limit: 1,
            filter: { locationRequestToken: { eq: cleanToken } },
          },
        });
        const found = result.data?.listRoadServiceCalls?.items?.[0] || null;
        if (!cancelled) {
          setCall(found);
          setError(found ? '' : 'This location link is no longer active.');
        }
      } catch (err) {
        if (!cancelled) setError('Could not open this location request.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadCall();
    return () => { cancelled = true; };
  }, [cleanToken]);

  async function sendLocation() {
    if (!call || submitting) return;
    if (!navigator.geolocation) {
      setError('This phone does not support browser location sharing.');
      return;
    }

    setSubmitting(true);
    setError('');
    setStatus('Waiting for your phone location...');

    navigator.geolocation.getCurrentPosition(
      async position => {
        const coords = position.coords;
        const timestamp = nowSeconds();
        try {
          await client.graphql({
            query: mutations.updateRoadServiceCall,
            variables: {
              input: {
                id: call.id,
                destinationLatitude: coords.latitude,
                destinationLongitude: coords.longitude,
                locationReceivedAt: timestamp,
                locationRequestStatus: 'RECEIVED',
                locationSource: 'driver-location-link',
              },
            },
          });

          await recordOperationEvent({
            eventType: EVENT_TYPES.STATUS_CHANGED,
            timestamp,
            locationId: call.locationId,
            assetId: call.assetId || null,
            workOrderId: call.workOrderId || call.id,
            technicianId: call.assignedTechnicianId || null,
            userId: null,
            previousStatus: call.status || null,
            newStatus: call.status || null,
            note: 'Driver shared road service location',
            relatedPhotoIds: [],
            metadata: JSON.stringify({
              source: 'driver-location-link',
              roadServiceCallId: call.id,
              accuracy: coords.accuracy,
            }),
          });

          setStatus('Location sent. Dispatch can see it now.');
          setCall(prev => prev ? {
            ...prev,
            destinationLatitude: coords.latitude,
            destinationLongitude: coords.longitude,
            locationReceivedAt: timestamp,
            locationRequestStatus: 'RECEIVED',
          } : prev);
        } catch (err) {
          setError('Could not send location. Please call dispatch.');
        } finally {
          setSubmitting(false);
        }
      },
      () => {
        setSubmitting(false);
        setStatus('');
        setError('Location permission was denied or unavailable. Please call dispatch.');
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 }
    );
  }

  return (
    <main className="rlp-page">
      <section className="rlp-panel">
        <div className="rlp-brand">REKON Road Service</div>
        {loading ? (
          <p className="rlp-muted">Opening location request...</p>
        ) : error && !call ? (
          <p className="rlp-error">{error}</p>
        ) : (
          <>
            <h1>Share Your Location</h1>
            <p className="rlp-copy">
              Dispatch requested your location for this road service call.
            </p>
            {call?.complaint && <p className="rlp-call">{call.complaint}</p>}
            <button className="rlp-button" onClick={sendLocation} disabled={submitting || call?.locationRequestStatus === 'RECEIVED'}>
              {submitting ? 'Sending...' : call?.locationRequestStatus === 'RECEIVED' ? 'Location Sent' : 'Send My Location'}
            </button>
            {status && <p className="rlp-success">{status}</p>}
            {error && <p className="rlp-error">{error}</p>}
            <p className="rlp-footnote">
              Your browser may ask for permission. REKON only uses this to place the service call on the dispatch map.
            </p>
          </>
        )}
      </section>
    </main>
  );
}
